import React, {Fragment, useContext, useMemo} from "react";
import DropdownMultiGroupSelect from "patient-ping-remedy/packages/dropdown-multi-group";
import {GenericObject} from "@/hooks/useForm";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {nonGroupedValues} from "@common/FilterComponent";
import {useQuery} from "@tanstack/react-query";
import {hasDischargeSummary, PingFlags, EventSettings}
  from "@/components/pages/settings/event_consumers/EventConsumers.constants";
import DropdownMultiSearchSelect, { IdNameType}
  from "@/components/pages/settings/event_consumers/DropdownMultiSearchSelect";
import {GroupTypes} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants";
import DropdownList from "patient-ping-remedy/packages/dropdown-list";
import Helpers from "@/helpers/helpers";

type Props = {
    form: GenericObject,
    handleChange: (event: (React.ChangeEvent | null), name: string, value: any) => void,
    isWithinModal?: boolean | undefined
}

const ADTFilterOptionsForm = (props: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);

  const {data: dispositionTypes} = useQuery({
    queryFn: () => carecoApi?.readDischargeDispositions(true),
    queryKey: ['dischargeDispositions', { extended: true }],
    enabled: !!carecoApi,
  });

  const dispositionTypeOptions = useMemo(() => {
    return dispositionTypes?.map(disposition => {
      return { id: disposition.value, value: disposition.value, label: disposition.label };
    }) || [];
  }, [dispositionTypes]);

  const eventFacilityTypeOptions: nonGroupedValues[] = useMemo(() => {
    return Object.keys(GroupTypes).map((type) => (
      { id: type, label: type, value: type}
    ));
  }, [GroupTypes]);

  const handleFilterChange = (filterKey: string, value: (string | IdNameType)[] | undefined) => {
    const updatedValues = {
      ...props.form?.filters.value,
      eventDetailFilters: {
        ...props.form?.filters.value.eventDetailFilters,
        ADT: {
          ...props.form?.filters.value.eventDetailFilters?.ADT,
          [filterKey]: { values: value ?? [] },
        },
      },
    };
    props.handleChange(null, 'filters', updatedValues);
  };

  async function handleDiagnosesSearch(search: string) {
    const diagnosisCodeResponses = await carecoApi!.searchDiagnosisCodes(search);
    return diagnosisCodeResponses.map(diagnose => {
      return { value: diagnose.full_code, label: diagnose.full_code + " - " + diagnose.synonym };
    });
  }

  const eventSettingsOptions: nonGroupedValues[] = useMemo(() => {
    return EventSettings.map(setting => ({
      id: setting,
      value: setting,
      label: setting
    }));
  }, []);


  return (
    <Fragment>
      <DropdownMultiGroupSelect
        id={"adt-facility-types"}
        label={"Event facility types"}
        options={eventFacilityTypeOptions}
        isWithinModal={props.isWithinModal}
        value={eventFacilityTypeOptions.filter(eventTypes =>
          props.form?.filters?.value?.eventDetailFilters?.ADT?.eventFacilityTypes?.values?.includes(eventTypes.value))}
        handleChange={(event) => handleFilterChange('eventFacilityTypes', event?.map(item => item.value), )}
      />

      <DropdownMultiGroupSelect
        id={"adt-discharge-dispositions"}
        label={"Discharge dispositions"}
        options={dispositionTypeOptions}
        isWithinModal={props.isWithinModal}
        value={dispositionTypeOptions.filter(eventTypes =>
          props.form?.filters?.value?.eventDetailFilters?.ADT?.dischargeDispositions?.values?.includes(eventTypes.value))}
        handleChange={(event) => handleFilterChange('dischargeDispositions', event?.map(item => item.value))}
      />

      <DropdownMultiGroupSelect
        id={"adt-settings"}
        label={"Settings"}
        options={eventSettingsOptions}
        isWithinModal={props.isWithinModal}
        value={eventSettingsOptions.filter(eventSetting =>
          (props.form?.filters?.value?.eventDetailFilters?.ADT?.settings?.values?.map((item: { name: any; }) => item.name) || [])
            .includes(eventSetting.value)
        )}
        handleChange={(event) =>
          handleFilterChange(
            'settings',
            event?.map(item => ({ name: item.value })) || []
          )
        }
      />

      <DropdownMultiSearchSelect
        label="Diagnoses"
        values={props.form?.filters.value.eventDetailFilters?.ADT?.diagnoses?.values || []}
        onChange={(values) => handleFilterChange('diagnoses', values)}
        handleSearch={handleDiagnosesSearch}
        isWithinModal={props.isWithinModal}
      />

      <DropdownMultiGroupSelect
        id={"adt-pingsFlags"}
        label={"Pings flags"}
        options={PingFlags}
        isWithinModal={props.isWithinModal}
        value={PingFlags.filter(eventTypes =>
          props.form?.filters?.value?.eventDetailFilters?.ADT?.pingsFlags?.values?.includes(eventTypes.value))}
        handleChange={(event) => handleFilterChange('pingsFlags', event?.map(item => item.value))}
      />
      <DropdownList
        id="has-discharge-summary"
        label="Has discharge summary?"
        isClearable
        isWithinModal={props.isWithinModal}
        items={hasDischargeSummary}
        value={hasDischargeSummary.filter(eventTypes =>
          props.form?.filters?.value?.eventDetailFilters?.ADT?.hasDischargeSummary === eventTypes.value)}
        handleChange={(value:any) => {
          props.handleChange(null, "filters", {
            ...props.form?.filters.value,
            eventDetailFilters: {
              ...props.form?.filters.value.eventDetailFilters,
              ADT: {
                ...props.form?.filters.value.eventDetailFilters?.ADT,
                ...(Helpers.isDefined(value?.value)
                  ? { hasDischargeSummary: value?.value }
                  : {}),
              },
            },
          });
          if (!Helpers.isDefined(value?.value)) {
            delete props.form?.filters.value.eventDetailFilters?.ADT.hasDischargeSummary;
          }
        }}
      />
    </Fragment>
  );
};

export default ADTFilterOptionsForm;
